/**
 * Aba "Relacionados" (Bloco D.3) — mostra de onde este card veio e o que ele
 * originou.
 *
 * Usado em DealDetailDialog (deal) e potencialmente em ServiceOrderDetail.
 * A aba só aparece se o hook retorna dados ou loading. (A flag
 * `CROSS_WORKFLOW_AUTOMATIONS_ENABLED` é env-var SÓ de backend — gate as
 * automações que CRIAM relations, não esta aba; comentário corrigido na
 * Rodada C-LIMPEZA 2026-07-10.)
 *
 * **FASE-C:** a tela inteira estava pintada com classes `mineral-*` que NÃO
 * existem no `tailwind.config.ts` — ou seja, texto e bordas ficavam sem cor
 * nenhuma, herdando o que viesse de cima. Trocado pelos tokens reais do design
 * system. Saíram também os emojis como ícone (🔗 🤖 👤 📋 🚫), o `rose-600`
 * fora da paleta, o rótulo "Deal" e o erro cru na cara do usuário.
 */

import { ArrowDownRight, ArrowUpLeft, Link2 } from 'lucide-react';

import { Badge } from '@/components/ui';
import { useCardRelations, RELATION_TYPE_LABELS, type CardType, type CardRelation } from '@/lib/card-relations-store';

import { formatarData } from '@/lib/format-date';
interface RelatedCardsTabProps {
  cardType: CardType;
  cardId: string;
}

function formatRelativeDate(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function RelationRow({ rel, direction }: { rel: CardRelation; direction: 'child' | 'parent' }) {
  const meta = RELATION_TYPE_LABELS[rel.relationType] ?? { label: 'Relacionado' };
  const Seta = direction === 'child' ? ArrowDownRight : ArrowUpLeft;
  return (
    <div
      data-testid={`related-card-${direction}-${rel.cardId}`}
      className="mb-2 flex min-w-0 items-start gap-3 rounded-xl border border-line p-3 dark:border-line-dark"
    >
      <Link2
        className="mt-0.5 h-4 w-4 shrink-0 text-ink-2 dark:text-ink-dark-2"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium">
          <Seta
            className="h-3.5 w-3.5 shrink-0 text-ink-2 dark:text-ink-dark-2"
            aria-hidden
          />
          <span className="min-w-0 truncate text-ink-1 dark:text-ink-dark-1">
            {rel.cardLabel}
          </span>
          <Badge variant="neutral" size="sm">
            {rel.cardType === 'deal' ? 'Negócio' : 'Ordem de serviço'}
          </Badge>
        </div>
        <div className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
          {meta.label} · {formatRelativeDate(rel.criadoEmIso)}
        </div>
      </div>
    </div>
  );
}

export function RelatedCardsTab({ cardType, cardId }: RelatedCardsTabProps) {
  const { data, isLoading, isError, error } = useCardRelations(cardType, cardId);

  if (isLoading) {
    return (
      <div
        data-testid="related-cards-loading"
        className="text-sm text-ink-2 dark:text-ink-dark-2"
      >
        Carregando as relações…
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="related-cards-error" className="text-sm text-red-deep dark:text-red-glow">
        Não foi possível carregar as relações deste card. Tente de novo daqui a pouco.
        <span className="mt-1 block text-xs text-ink-2 dark:text-ink-dark-2">
          Detalhe: {error instanceof Error ? error.message : 'motivo não informado'}
        </span>
      </div>
    );
  }

  const children = data?.children ?? [];
  const parents = data?.parents ?? [];

  if (children.length === 0 && parents.length === 0) {
    return (
      <div
        data-testid="related-cards-empty"
        className="py-6 text-center text-sm text-ink-2 dark:text-ink-dark-2"
      >
        Este card ainda não tem ligação com nenhum outro.
      </div>
    );
  }

  return (
    <div data-testid="related-cards-list">
      {parents.length > 0 && (
        <section className="mb-6">
          <h3 className="mb-2 text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
            Originado por ({parents.length})
          </h3>
          {parents.map((rel) => (
            <RelationRow key={`parent-${rel.id}`} rel={rel} direction="parent" />
          ))}
        </section>
      )}

      {children.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
            Originou ({children.length})
          </h3>
          {children.map((rel) => (
            <RelationRow key={`child-${rel.id}`} rel={rel} direction="child" />
          ))}
        </section>
      )}
    </div>
  );
}
